import { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/feature/Pagination";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 3;

export default function Category() {
  const { categoryName } = useParams();
  const category = categories.find((item) => item.slug === categoryName);

  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [activeTag, setActiveTag] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setActiveTag("all");
    setPage(1);
  }, [categoryName]);

  useEffect(() => {
    setPage(1);
  }, [sort, activeTag]);

  const list = useMemo(
    () => articles.filter((item) => item.category === categoryName),
    [categoryName]
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    list.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, [list]);

  const filtered = useMemo(() => {
    const result = list.filter((item) => activeTag === "all" || item.tags.includes(activeTag));
    return result.sort((a, b) =>
      sort === "latest" ? b.date.localeCompare(a.date) : b.views - a.views
    );
  }, [list, activeTag, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useSeo({
    title: category
      ? `${category.name} - 黑人文化资讯 - 黑人`
      : "分类未找到 - 黑人文化资讯 - 黑人",
    description: category
      ? category.description
      : "未找到对应分类，请返回首页浏览黑人文化的多个领域内容。",
    keywords: category ? category.keywords : "黑人文化,黑人资讯",
  });

  if (!category) {
    return (
      <div className="mx-auto max-w-content px-4 md:px-8 py-20 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl" />
        </div>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-900">分类不存在</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的分类可能已被移动或删除。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="relative h-64 md:h-72 overflow-hidden">
        <img
          src={category.cover}
          alt={category.name}
          title={`${category.name}分类 - 黑人文化资讯`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/70 to-primary-950/40" />
        <div className="relative z-10 mx-auto max-w-content h-full px-4 md:px-8 flex flex-col justify-end pb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-11 h-11 rounded-md bg-background-50/15 backdrop-blur text-background-50 flex items-center justify-center">
              <i className={`${category.icon} text-xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
                {category.name}
              </h1>
              <p className="text-xs tracking-widest text-background-300">{category.en}</p>
            </div>
          </div>
          <p className="text-sm text-background-200 max-w-3xl leading-relaxed">{category.description}</p>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 md:px-8 py-8 md:py-12">
        <div className="bg-background-50 rounded-lg border border-background-200 px-4 py-3 mb-8">
          <Breadcrumb items={[{ label: "分类" }, { label: category.name }]} />
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500 mr-1">标签：</span>
            <button
              type="button"
              onClick={() => setActiveTag("all")}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                activeTag === "all"
                  ? "bg-primary-600 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
              }`}
            >
              全部
            </button>
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  activeTag === tag
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-foreground-500">排序：</span>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                type="button"
                onClick={() => setSort("latest")}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === "latest" ? "bg-background-50 text-primary-700 shadow-sm" : "text-foreground-500"
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => setSort("hot")}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === "hot" ? "bg-background-50 text-primary-700 shadow-sm" : "text-foreground-500"
                }`}
              >
                最热
              </button>
            </div>
          </div>
        </div>

        <p className="text-sm text-foreground-500 mb-6">
          共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 篇相关资讯
        </p>

        {paged.length > 0 ? (
          <>
            <div className="space-y-4">
              {paged.map((article) => (
                <ArticleCard key={article.id} article={article} variant="horizontal" />
              ))}
            </div>
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="py-20 text-center bg-background-50 rounded-lg border border-background-200">
            <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
              <i className="ri-inbox-line text-3xl" />
            </div>
            <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-800">暂无相关内容</h3>
            <p className="mt-2 text-sm text-foreground-500">换个标签或分类看看吧。</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                去搜索
              </Link>
            </div>
          </div>
        )}

        <div className="mt-14">
          <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">其它分类</h3>
          <div className="flex flex-wrap gap-3">
            {categories
              .filter((item) => item.slug !== category.slug)
              .map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${item.icon} text-accent-500`} />
                  {item.name}
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}